import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import { broadcastLabels } from "@/mocks/broadcasts";

/* Модальное окно «Новая рассылка»: название (обязательно) и метки.
   Рассылка создаётся черновиком — текст и аудиторию настраивают дальше. */

const FIELD =
  "w-full box-border rounded-btn border border-border bg-background px-3 text-[12.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

type Props = {
  onClose: () => void;
  onCreate: (name: string, labels: string[]) => void;
  onLearnMore: () => void;
};

export default function BroadcastPanel({ onClose, onCreate, onLearnMore }: Props) {
  const [name, setName] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const wrap = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const ready = name.trim().length >= 2;

  const toggleLabel = (label: string) => {
    setLabels((prev) => (prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]));
  };

  const create = () => {
    setTouched(true);
    if (!ready) return;
    onCreate(name.trim(), labels);
  };

  return (
    <div className="absolute inset-0 z-[70] flex items-center justify-center p-4">
      <button type="button" onClick={onClose} aria-label={t("Закрыть окно")} className="absolute inset-0 cursor-pointer bg-black/45 backdrop-blur-[1px]" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="broadcast-panel-title"
        className="relative flex w-full max-w-[26rem] flex-col overflow-hidden rounded-card border border-border bg-card shadow-lift"
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <h4 id="broadcast-panel-title" className="min-w-0 flex-1 truncate text-[13.5px] font-semibold">
            {t("Новая рассылка")}
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть окно")}
            className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="px-4 py-3.5">
          <label className="block">
            <span className="text-[11.5px] font-semibold">{t("Название рассылки")}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
              placeholder={t("Название видно только внутри рабочего пространства")}
              aria-invalid={touched && !ready}
              className={cn(FIELD, "mt-1 h-9", touched && !ready && "border-red-500/60 focus:border-red-500 focus:ring-red-500/20")}
            />
            {touched && !ready ? <span className="mt-1 block text-[10.5px] text-red-600">{t("Введите название — от 2 символов")}</span> : null}
          </label>

          <div className="mt-3.5" ref={wrap}>
            <span className="flex items-center gap-1.5 text-[11.5px] font-semibold">
              {t("Метки")}
              <span className="font-normal text-muted-foreground">{t("(необязательно)")}</span>
              <span
                title={t("Метки помогают находить рассылки в списке и фильтровать их")}
                className="grid size-4 cursor-help place-items-center rounded-full border border-border text-[9px] leading-none text-muted-foreground"
                aria-hidden
              >
                i
              </span>
            </span>

            <div className="relative mt-1">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="listbox"
                className={cn(
                  "flex min-h-9 w-full cursor-pointer flex-wrap items-center gap-1 rounded-btn border bg-background px-2.5 py-1.5 text-start transition",
                  open ? "border-primary ring-2 ring-primary/25" : "border-border hover:border-primary/50",
                )}
              >
                {labels.length === 0 ? (
                  <span className="flex-1 truncate text-[12.5px] text-muted-foreground/70">{t("Добавить метки к рассылке")}</span>
                ) : (
                  <span className="flex min-w-0 flex-1 flex-wrap gap-1">
                    {labels.map((l) => (
                      <span key={l} className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-primary">
                        <span className="truncate">{l}</span>
                        <span
                          role="button"
                          tabIndex={-1}
                          aria-label={t("Убрать метку {label}", { label: l })}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLabel(l);
                          }}
                          className="grid size-3.5 flex-none place-items-center rounded-full transition hover:bg-primary/20"
                        >
                          <X className="size-2.5" aria-hidden />
                        </span>
                      </span>
                    ))}
                  </span>
                )}
                <ChevronDown className={cn("size-3.5 flex-none text-muted-foreground transition", open && "rotate-180")} aria-hidden />
              </button>

              {open ? (
                <div role="listbox" aria-multiselectable className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-btn border border-border bg-card p-1 shadow-lift">
                  {broadcastLabels.map((l) => {
                    const active = labels.includes(l);
                    return (
                      <button
                        key={l}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => toggleLabel(l)}
                        className={cn(
                          "flex w-full cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start text-[11.5px] transition hover:bg-muted",
                          active ? "font-semibold text-foreground" : "text-foreground/75",
                        )}
                      >
                        <span
                          className={cn(
                            "grid size-3.5 flex-none place-items-center rounded-[4px] border transition",
                            active ? "border-primary bg-primary text-primary-foreground" : "border-border",
                          )}
                          aria-hidden
                        >
                          {active ? <Check className="size-2.5" strokeWidth={3} /> : null}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{l}</span>
                      </button>
                    );
                  })}
                  <p className="mt-1 border-t border-border px-2 pb-0.5 pt-1.5 text-[10px] leading-relaxed text-muted-foreground">
                    {t("Метки можно поменять в любой момент — в черновике или после отправки.")}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 border-t border-border px-4 py-2.5">
          <button
            type="button"
            onClick={onLearnMore}
            className="cursor-pointer whitespace-nowrap text-[11.5px] font-semibold text-primary underline decoration-dotted underline-offset-2 transition hover:brightness-110"
          >
            {t("Узнать больше")}
          </button>
          <div className="ms-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn px-3 text-[12.5px] font-semibold text-muted-foreground transition hover:text-foreground"
            >
              {t("Отмена")}
            </button>
            <button
              type="button"
              onClick={create}
              aria-disabled={!ready}
              className={cn(
                "inline-flex h-8 items-center justify-center whitespace-nowrap rounded-btn px-4 text-[12.5px] font-semibold transition",
                ready ? "cursor-pointer bg-primary text-primary-foreground hover:brightness-110" : "cursor-not-allowed bg-foreground/10 text-muted-foreground",
              )}
            >
              {t("Создать")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}